(() => {
  if (customElements.get('jp-newsletter-offer')) return;

  const STORAGE_KEY = 'nelpanda:jp-newsletter-offer';
  const STATE_EVENT = 'nelpanda:jp-newsletter-offer-state';
  const STATE_TTL = 180 * 24 * 60 * 60 * 1000;
  let memoryState = null;
  let activeOffer = null;
  let formOpen = false;

  class NewsletterOffer extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-offer-trigger]');
      this.badge = this.querySelector('[data-offer-badge]');
      this.tooltip = this.querySelector('[data-offer-tooltip]');
      this.dialog = this.querySelector('dialog');
      this.initialLabel = this.trigger.getAttribute('aria-label');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.trigger.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-offer-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.trigger.focus(), options);
      this.badge.addEventListener('animationend', () => this.classList.remove('is-shaking'), options);
      window.addEventListener('klaviyoForms', (event) => this.handleFormEvent(event.detail || {}), options);
      window.addEventListener(STATE_EVENT, () => this.syncState(), options);
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY || event.key === null) {
          memoryState = null;
          this.syncState();
        }
      }, options);
      this.syncState();
    }

    disconnectedCallback() {
      this.listeners.abort();
      this.resetOpening();
      if (activeOffer === this) activeOffer = null;
    }

    readState() {
      let state = memoryState;
      try {
        state = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || memoryState;
      } catch (_) {}
      return [this.dataset.formId, this.dataset.otherFormId].includes(state?.formId) &&
        state?.status === 'submitted' && Number(state.expiresAt) > Date.now();
    }

    syncState() {
      this.submitted = Boolean(this.readState());
      this.badge.hidden = false;
      if (this.submitted) this.classList.remove('is-shaking');
      const label = this.submitted ? this.dialog.getAttribute('aria-label') : this.initialLabel;
      this.trigger.setAttribute('aria-label', label);
      this.tooltip.textContent = label;
    }

    open() {
      this.syncState();
      if (this.submitted) {
        if (!this.dialog.open) {
          this.dialog.showModal();
          this.dialog.focus({ preventScroll: true });
        }
        return;
      }
      if (!this.dataset.formId || activeOffer?.opening || formOpen) return;
      activeOffer = this;
      this.opening = true;
      this.trigger.disabled = true;
      this.trigger.setAttribute('aria-busy', 'true');
      this.openTimer = window.setTimeout(() => {
        this.resetOpening();
        if (activeOffer === this) activeOffer = null;
      }, 8000);
      try {
        if (typeof window.klaviyo?.openForm === 'function') {
          window.klaviyo.openForm(this.dataset.formId);
        } else {
          window._klOnsite = window._klOnsite || [];
          window._klOnsite.push(['openForm', this.dataset.formId]);
        }
      } catch (_) {
        this.resetOpening();
        activeOffer = null;
      }
    }

    resetOpening() {
      window.clearTimeout(this.openTimer);
      this.opening = false;
      this.trigger.disabled = false;
      this.trigger.setAttribute('aria-busy', 'false');
    }

    handleFormEvent(detail) {
      if (detail.formId !== this.dataset.formId) return;
      if (detail.type === 'submit') {
        if (!this.readState()) {
          memoryState = {
            formId: this.dataset.formId,
            status: 'submitted',
            expiresAt: Date.now() + STATE_TTL,
          };
          try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryState));
          } catch (_) {}
          window.dispatchEvent(new Event(STATE_EVENT));
        }
      } else if (detail.type === 'open') {
        formOpen = true;
        this.resetOpening();
      } else if (detail.type === 'close') {
        formOpen = false;
        this.resetOpening();
        this.syncState();
        if (!this.submitted && this.getClientRects().length) this.classList.add('is-shaking');
        if (activeOffer === this) {
          activeOffer = null;
          this.trigger.focus();
        }
      }
    }
  }

  customElements.define('jp-newsletter-offer', NewsletterOffer);
})();
